// `LoginPage` — US-004 Log in.
//
// DESIGN.md §1.2, §4.2 + O8 (unverified email → /auth/verify-pending),
// O10 (`localStorage.lastRememberMe`).

import { useEffect, useId, useRef, useState } from 'react';

import { ApiError, use2faChallenge, useSignIn } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Checkbox, Field, Input, toast } from '@barel/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { AuthLayout } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { fieldError } from './lib/localizeError';
import { notifyExtensionLogin } from './lib/notifyExtensionLogin';
import { signInFormSchema, type SignInFormInput } from './lib/schemas';
import { emit } from './lib/telemetry';

const LAST_REMEMBER_KEY = 'lastRememberMe';

function readRememberMe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LAST_REMEMBER_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeRememberMe(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_REMEMBER_KEY, value ? 'true' : 'false');
  } catch {
    // ignore quota / private mode
  }
}

interface LoginSearch {
  from?: string;
  reset?: string;
}

export function LoginPage() {
  const t = useT('auth');
  const navigate = useNavigate();
  const signIn = useSignIn();
  const verify2fa = use2faChallenge();
  const search = useSearch({ strict: false }) as LoginSearch;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormInput>({
    resolver: zodResolver(signInFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: readRememberMe(),
    },
  });

  const [bannerError, setBannerError] = useState<string | null>(null);
  const [rateLimitedSeconds, setRateLimitedSeconds] = useState<number>(0);
  // 2FA challenge state — populated when sign-in returns `twoFactorRedirect`.
  const [twoFaChallenge, setTwoFaChallenge] = useState<boolean>(false);
  const [totpCode, setTotpCode] = useState<string>('');
  const [twoFaError, setTwoFaError] = useState<string | null>(null);
  // The userId returned by `sign-in/email`. Captured even when the 2FA
  // gate intercepts the immediate redirect so we can post `ls.login` to
  // the extension after the TOTP challenge completes (same user, same id).
  const pendingUserIdRef = useRef<string | null>(null);

  // `?reset=1` → toast on first paint then strip the query so a refresh
  // doesn't re-fire it.
  const resetToastFiredRef = useRef<boolean>(false);
  useEffect(() => {
    if (resetToastFiredRef.current) return;
    if (search.reset === '1') {
      resetToastFiredRef.current = true;
      toast.success({ title: t('resetPassword.complete.successToast') });
      void navigate({
        to: '/auth/login',
        search: { from: search.from },
        replace: true,
      });
    }
  }, [search.from, search.reset, navigate, t]);

  // Rate-limit countdown — ticks once per second while >0. The submit button
  // is disabled while the countdown is active.
  useEffect(() => {
    if (rateLimitedSeconds <= 0) return;
    const handle = setTimeout(() => {
      setRateLimitedSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => {
      clearTimeout(handle);
    };
  }, [rateLimitedSeconds]);

  const onSubmit: SubmitHandler<SignInFormInput> = async (values) => {
    setBannerError(null);
    emit({ name: 'auth_signin_submitted' });

    writeRememberMe(values.rememberMe === true);

    try {
      const result = await signIn.mutateAsync({
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
      });
      // Hold the userId for the 2FA branch — same user across the TOTP step.
      pendingUserIdRef.current = result?.user?.id ?? null;
      // BA flags 2FA-required logins with `twoFactorRedirect: true`. Show the
      // TOTP challenge in-place; success there issues the full session.
      if (result?.twoFactorRedirect) {
        setTwoFaChallenge(true);
        setTwoFaError(null);
        return;
      }
      if (result?.user?.id) {
        notifyExtensionLogin(result.user.id);
      }
      void navigate({
        to: search.from && !search.from.startsWith('/auth') ? search.from : '/',
        replace: true,
      });
    } catch (err) {
      if (ApiError.is(err)) {
        if (err.code === 'auth.email_not_verified') {
          emit({ name: 'auth_signin_failed', props: { reason: 'unverified' } });
          void navigate({
            to: '/auth/verify-pending',
            search: { email: values.email },
          });
          return;
        }
        if (err.httpStatus === 429 || err.code === 'auth.rate_limited') {
          emit({ name: 'auth_signin_failed', props: { reason: 'rate_limited' } });
          setRateLimitedSeconds(60);
          setBannerError(null);
          return;
        }
        if (err.httpStatus === 401 || err.code === 'auth.bad_credentials') {
          emit({ name: 'auth_signin_failed', props: { reason: 'bad_credentials' } });
          setBannerError(t('signIn.errors.badCredentials'));
          return;
        }
      }
      emit({ name: 'auth_signin_failed', props: { reason: 'unknown' } });
      setBannerError(t('signIn.errors.generic'));
    }
  };

  const formId = useId();
  const titleId = `${formId}-title`;
  const submitDisabled = rateLimitedSeconds > 0;

  const emailError = fieldError(errors.email, t('fieldErrors.email.invalid'));
  const passwordError = fieldError(errors.password, t('fieldErrors.required'));

  return (
    <AuthLayout brandHeadline="brandHeadlineLogin">
      <section data-route="auth/login" aria-labelledby={titleId} className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 id={titleId} className="text-xl font-semibold text-primary">
            {t('signIn.title')}
          </h1>
          <p className="text-sm text-secondary">{t('signIn.subtitle')}</p>
        </header>

        {rateLimitedSeconds > 0 ? (
          <Banner tone="warning">
            {t('signIn.errors.rateLimited', { seconds: rateLimitedSeconds })}
          </Banner>
        ) : null}
        {bannerError ? <Banner tone="error">{bannerError}</Banner> : null}

        {twoFaChallenge ? (
          <section
            data-region="totp-challenge"
            aria-label={t('twoFactor.title')}
            className="flex flex-col gap-3 rounded-lg border border-default bg-bg-elevated p-4"
          >
            <h2 className="text-base font-semibold text-primary">{t('twoFactor.title')}</h2>
            <p className="text-sm text-secondary">{t('twoFactor.subtitle')}</p>
            {twoFaError ? <Banner tone="error">{twoFaError}</Banner> : null}
            <Field required>
              <Field.Label>{t('twoFactor.codeLabel')}</Field.Label>
              <Field.Control>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={totpCode}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setTotpCode(event.target.value.replace(/\D/gu, ''))
                  }
                  data-testid="login-totp-code"
                />
              </Field.Control>
            </Field>
            <Button
              type="button"
              variant="primary"
              size="md"
              loading={verify2fa.isPending}
              disabled={totpCode.length < 6}
              data-testid="login-totp-submit"
              onClick={async () => {
                setTwoFaError(null);
                try {
                  await verify2fa.mutateAsync({ code: totpCode });
                  if (pendingUserIdRef.current) {
                    notifyExtensionLogin(pendingUserIdRef.current);
                  }
                  void navigate({
                    to: search.from && !search.from.startsWith('/auth') ? search.from : '/',
                    replace: true,
                  });
                } catch (err) {
                  setTwoFaError(
                    ApiError.is(err) && err.httpStatus === 400
                      ? t('twoFactor.errors.badCode')
                      : t('twoFactor.errors.generic'),
                  );
                }
              }}
            >
              {t('twoFactor.submit')}
            </Button>
          </section>
        ) : null}

        <form
          aria-busy={isSubmitting || undefined}
          className="flex flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          data-testid="login-form"
          hidden={twoFaChallenge}
          aria-hidden={twoFaChallenge ? true : undefined}
        >
          <Field invalid={!!emailError} required>
            <Field.Label>{t('signIn.fields.email.label')}</Field.Label>
            <Field.Control>
              <Input
                {...register('email')}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('signIn.fields.email.placeholder')}
                data-testid="login-email"
              />
            </Field.Control>
            {emailError ? <Field.Error>{emailError}</Field.Error> : null}
          </Field>

          <Field invalid={!!passwordError} required>
            <Field.Label>{t('signIn.fields.password.label')}</Field.Label>
            <Field.Control>
              <PasswordField
                {...register('password')}
                autoComplete="current-password"
                data-testid="login-password"
                toggleShowLabel={t('signIn.fields.password.toggleShow')}
                toggleHideLabel={t('signIn.fields.password.toggleHide')}
                toggleTestId="login-password-toggle"
              />
            </Field.Control>
            {passwordError ? <Field.Error>{passwordError}</Field.Error> : null}
          </Field>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-secondary">
              <Checkbox
                {...register('rememberMe')}
                onCheckedChange={(value) => {
                  setValue('rememberMe', value === true);
                }}
                defaultChecked={readRememberMe()}
                aria-label={t('signIn.rememberMe')}
                data-testid="login-remember"
              />
              <span>{t('signIn.rememberMe')}</span>
            </label>
            <Link
              to="/auth/forgot"
              className="text-sm text-secondary underline hover:text-primary"
              data-testid="login-forgot-link"
            >
              {t('signIn.forgotPassword')}
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isSubmitting}
            disabled={submitDisabled}
            data-testid="login-submit"
          >
            {submitDisabled
              ? t('signIn.errors.rateLimited', { seconds: rateLimitedSeconds })
              : t('signIn.submit')}
          </Button>
        </form>

        <p className="text-sm text-secondary">
          {t('signIn.noAccount')}{' '}
          <Link
            to="/auth/signup"
            className="font-medium text-primary underline"
            data-testid="login-signup-link"
          >
            {t('signIn.createAccount')}
          </Link>
        </p>
      </section>
    </AuthLayout>
  );
}
